import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { API_BASE_URL, getApiUrl } from '../config/api';

interface User {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'branch_manager' | 'inventory_manager';
  branchId?: string;
  lastLogin?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // API_BASE_URL is now imported from centralized config
  console.log('🔗 AuthContext API_BASE_URL:', API_BASE_URL);
  console.log('🔗 AuthContext Test getApiUrl:', getApiUrl('auth/login'));

  // Initialize authentication state
  useEffect(() => {
    const initializeAuth = async () => {
      console.log('🔐 Auth initialization starting...');
      try {
        const storedToken = localStorage.getItem('ihub_token') || sessionStorage.getItem('ihub_token');
        console.log('🔐 Stored token found:', !!storedToken);
        
        if (storedToken) {
          // Verify token with backend
          const verifyUrl = getApiUrl('auth/verify');
          console.log('🔗 Token verify URL:', verifyUrl);
          const response = await fetch(verifyUrl, {
            headers: {
              'Authorization': `Bearer ${storedToken}`,
              'Content-Type': 'application/json'
            }
          });

          console.log('🔐 Token verification response status:', response.status);
          if (response.ok) {
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
              const data = await response.json();
              console.log('🔐 Token verification successful, setting user:', data.user?.email);
              setUser(data.user);
              setToken(storedToken);
            } else {
              localStorage.removeItem('ihub_token');
              sessionStorage.removeItem('ihub_token');
            }
          } else {
            console.log('🔐 Token verification failed, clearing storage');
            // Token is invalid, clear storage
            localStorage.removeItem('ihub_token');
            sessionStorage.removeItem('ihub_token');
          }
        } else {
          console.log('🔐 No stored token found');
        }
      } catch (error) {
        console.error('🔐 Auth initialization error:', error);
        // Clear invalid tokens
        localStorage.removeItem('ihub_token');
        sessionStorage.removeItem('ihub_token');
      } finally {
        console.log('🔐 Auth initialization complete, setting isLoading to false');
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = async (email: string, password: string, rememberMe = false): Promise<{ success: boolean; error?: string }> => {
    try {
      const loginUrl = getApiUrl('auth/login');
      console.log('🔗 Login API URL (getApiUrl):', loginUrl);
      console.log('🔗 Login API URL (direct):', `${API_BASE_URL}/api/auth/login`);
      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      // Handle license verification failure (503) or server down (502)
      if (response.status === 503) {
        const text = await response.text();
        try {
          const errData = JSON.parse(text);
          if (errData.error?.includes('License')) {
            return { success: false, error: 'License verification failed. Please contact the administrator.' };
          }
          return { success: false, error: errData.message || 'Service unavailable. Please try again later.' };
        } catch {
          return { success: false, error: 'Service unavailable. Please try again later.' };
        }
      }

      if (response.status === 502) {
        return { success: false, error: 'Server is not running. Please contact the administrator.' };
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        return { success: false, error: 'Server error. Please contact the administrator.' };
      }

      const data = await response.json();

      if (response.ok && data.success) {
        setUser(data.user);
        setToken(data.token);

        // Store token based on remember me preference
        if (rememberMe) {
          localStorage.setItem('ihub_token', data.token);
        } else {
          sessionStorage.setItem('ihub_token', data.token);
        }

        return { success: true };
      } else {
        return { success: false, error: data.error || 'Login failed' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Unable to connect to the server. Please check your connection.' };
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('ihub_token');
    sessionStorage.removeItem('ihub_token');
    
    // Optional: Call logout endpoint for logging purposes
    if (token) {
      const logoutUrl = getApiUrl('auth/logout');
      fetch(logoutUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }).catch(error => console.error('Logout API call failed:', error));
    }
  };

  const refreshUser = async () => {
    if (!token) return;

    try {
      const refreshUrl = getApiUrl('auth/verify');
      const response = await fetch(refreshUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
      } else {
        // Token is invalid, logout user
        logout();
      }
    } catch (error) {
      console.error('User refresh error:', error);
      logout();
    }
  };

  const value: AuthContextType = {
    user,
    token,
    isLoading,
    isAuthenticated: !!user && !!token,
    login,
    logout,
    refreshUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;